import { useHeaderOverlay } from '../lib/HeaderOverlayContext';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useOverlayFocus } from '../lib/useOverlayFocus';
import { navigationBottom } from '../lib/overlayPosition';
import { useWorkspacePath } from '../lib/workspaceRoutes';

export function ToolsMenu() {
  const workspaceTo = useWorkspacePath();
  const [open, setOpen] = useHeaderOverlay('tools');
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 16, top: 0, maxHeight: 0 });
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const anchor = triggerRef.current?.getBoundingClientRect();
      if (!anchor) return;
      const width = Math.min(1040, innerWidth - 32);
      const top = Math.max(anchor.bottom, navigationBottom()) + 8;
      setPosition({ left: Math.max(16, Math.min(anchor.right - width, innerWidth - width - 16)), top, maxHeight: Math.max(0, innerHeight - top - 16) });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  useOverlayFocus({
    open,
    panelRef,
    triggerRef,
    preserveExternalFocus: true,
    onClose: () => setOpen(false),
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node) && !panelRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('mousedown', onClick);
    };
  }, [open, setOpen]);

  return (
    <>
      <div ref={ref} className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label="Tools"
          className="flex h-9 items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 text-xs font-medium text-white/75 transition-colors hover:bg-white/[0.10] hover:text-white"
        >
          <GearIcon />
          <span>Tools</span>
          <ChevronIcon open={open} />
        </button>

        {open && createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Starmap tools"
            style={{ ...position, overflowY: 'auto' }}
            className="fixed z-30 w-[min(1040px,calc(100vw-32px))] overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] text-[var(--text)] shadow-[var(--shadow-md)]"
          >
            <div className="border-b border-[var(--border)] px-5 py-4">
              <div className="text-xs text-[var(--text-secondary)]">Tools</div>
              <div className="mt-1 text-base text-[var(--text)]">What do you need to do?</div>
            </div>
            <div className="grid gap-3 p-4 md:grid-cols-3">
              <ToolCard
                eyebrow="Intro"
                title="How do I get an intro?"
                body="I need to reach a specific person. Show me the warmest path and who can help."
                to={workspaceTo('/paths')}
                onSelect={() => setOpen(false)}
              />
              <ToolCard
                eyebrow="Travel"
                title="Who’s in town?"
                body="I’m visiting an office and want to know who I should meet while I’m there."
                to={workspaceTo('/town')}
                onSelect={() => setOpen(false)}
              />
              <ToolCard
                eyebrow="Coverage"
                title="What do I cover?"
                body="AWS asked me to send my coverage. Generate a clean summary here."
                to={workspaceTo('/coverage')}
                onSelect={() => setOpen(false)}
              />
              <ToolCard
                eyebrow="Team"
                title="How’s my team tracking?"
                body="Compare the reps under a manager — team health, coverage, and quarter-over-quarter trend."
                to={workspaceTo('/manager')}
                onSelect={() => setOpen(false)}
              />
              <ToolCard
                eyebrow="History"
                title="What changed since last quarter?"
                body="Frozen engagement snapshots per quarter — see what improved or slipped."
                to={workspaceTo('/snapshots')}
                onSelect={() => setOpen(false)}
              />
            </div>
          </div>, document.body,
        )}
      </div>
    </>
  );
}

function ToolCard({
  eyebrow,
  title,
  body,
  to,
  onSelect,
}: {
  eyebrow: string;
  title: string;
  body: string;
  to: string;
  onSelect?: () => void;
}) {
  const content = (
    <>
      <div className="text-xs text-[var(--text-secondary)]">{eyebrow}</div>
      <div className="mt-2 text-lg font-medium leading-6 text-[var(--text)]">{title}</div>
      <p className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">{body}</p>
    </>
  );
  const className =
    'group block min-h-[142px] rounded-lg border border-[var(--border)] bg-[var(--surface)] p-5 text-left transition-colors hover:border-[var(--border-hover)] hover:bg-[var(--caylent-bg)]';

  return (
    <Link to={to} onClick={onSelect} className={className}>
      {content}
    </Link>
  );
}

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
      <path
        d="M19.4 13.5a7.5 7.5 0 0 0 0-3l2-1.6-2-3.4-2.4 1a7.5 7.5 0 0 0-2.6-1.5L14 2h-4l-.4 2.6a7.5 7.5 0 0 0-2.6 1.5l-2.4-1-2 3.4 2 1.6a7.5 7.5 0 0 0 0 3l-2 1.6 2 3.4 2.4-1a7.5 7.5 0 0 0 2.6 1.5L10 22h4l.4-2.6a7.5 7.5 0 0 0 2.6-1.5l2.4 1 2-3.4-2-1.6Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 12 8"
      className={`h-2.5 w-2.5 text-[var(--text-secondary)] transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none"
      aria-hidden
    >
      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
